import React, { useState, useEffect } from 'react';
import Joyride, { CallBackProps, STATUS, Step } from 'react-joyride';
import parse from 'html-react-parser';
import useWindowWidth from '../../../hooks/useWindowWidth';
import { joyrideStyles, joyrideStylesDark } from '../../../utils/joyrideStyles';
import { RootState } from '../../../store/store';
import { useSelector } from 'react-redux';
import { examsSelectors } from '../../../slices/exams/examsSlice';
// import { useSystemTheme } from '../../../hooks/useSystemTheme';

interface HomeFeatureTourProps {
  stepTargets: (React.RefObject<HTMLElement> | string)[];
  stepFunctions: (() => void)[];
  run: boolean;
  stepIndex: number;
  onStepChange: (newIndex: number) => void;
  onTourEnd?: () => void;
  handleSeenHomeTour: () => void;
}

const HomeFeatureTour: React.FC<HomeFeatureTourProps> = ({
  stepTargets,
  stepFunctions,
  run,
  stepIndex,
  onStepChange,
  onTourEnd,
  handleSeenHomeTour,
}) => {
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const primaryExam = useSelector(examsSelectors.selectPrimaryExam);

  // const systemTheme = useSystemTheme();

  const [steps, setSteps] = useState<Step[]>([]);
  const [ready, setReady] = useState(false);
  const windowWidth = useWindowWidth();
  const placementsDesktop = ['right', 'bottom', 'bottom', 'bottom', 'right'];
  const placementsMobile = ['center', 'auto', 'auto', 'auto', 'top'];
  const styles =
    currentUser && currentUser.theme === 'dark'
      ? // || (currentUser && currentUser.theme === 'system' && systemTheme === 'dark')
        joyrideStylesDark
      : joyrideStyles;

  const renderContents = (index: number) => {
    const isCustomExam = primaryExam?.type === 'CUSTOM';
    const isDesktop = windowWidth > 900;

    const contentMap: {
      custom: { [key: number]: string };
      standard: { [key: number]: string };
    } = {
      custom: {
        0: `Ora generates one daily session to help you master every topic before your exam.<br/><br/>We've kept this one short to get youoriented.`,
        1: 'Let&apos;s start your first session!',
      },
      standard: {
        0: `Ora generates one daily session to help you master every topic before your exam.<br/><br/>We've kept this one short to get you oriented.`,
        1: `Ora has planned out every day until your exam. ${isDesktop ? 'Hover the timeline to see your topics for each day.' : ''}`,
        2: 'Let&apos;s start your first session!',
      },
    };

    const content = isCustomExam
      ? contentMap.custom[index]
      : contentMap.standard[index];
    return content ? parse(content) : null;
  };

  useEffect(() => {
    return () => {
      document.body.classList.remove('show-header-buttons');
    };
  }, []);

  useEffect(() => {
    if (stepIndex === steps.length - 1) {
      handleSeenHomeTour();
    }
  }, [stepIndex]);

  // Trigger the tour when the targets are ready
  useEffect(() => {
    const areTargetsReady = stepTargets.every((target) => {
      if (typeof target === 'string') {
        return true;
      } else {
        return target.current !== null;
      }
    });
    if (areTargetsReady) {
      // @ts-expect-error - TS doesn't know about all step props
      const newSteps: Step[] = stepTargets.map((target, index) => {
        const stepTarget =
          typeof target === 'string' ? target : target.current!;
        return {
          target: stepTarget,
          placement:
            windowWidth > 900
              ? placementsDesktop[index]
              : placementsMobile[index],
          disableBeacon: true,
          content: (
            <div className="tour-wrapper">
              <div className="tour-wrapper_header">
                <div className="step-number">
                  {index + 1} <span>of {stepTargets.length}</span>
                </div>
                <div className="steps">
                  {[...Array(stepTargets.length)].map((_, i) => (
                    <div
                      key={i}
                      className={`steps_step ${i === index ? 'active' : ''}`}
                    ></div>
                  ))}
                </div>
              </div>
              <div className="tour-wrapper_content">
                {renderContents(index)}
              </div>
              <div className="blur-1"></div>
              <div className="blur-2"></div>
              <div className="tour-wrapper_footer">
                {(index === 0 || index === 1) && (
                  <>
                    <button
                      onClick={() => {
                        onStepChange(stepIndex + 1);
                        const floater = document.querySelector(
                          '.__floater'
                        ) as HTMLDivElement;
                        if (floater) {
                          floater.style.display = 'none';
                        }
                      }}
                      className="button button--glass"
                    >
                      Next
                    </button>
                    <button
                      className="button button--skip"
                      onClick={() => onTourEnd?.()}
                    >
                      Skip Tour
                    </button>
                  </>
                )}
                {index === 2 && (
                  <button
                    onClick={() => {
                      onTourEnd?.();
                      const floater = document.querySelector(
                        '.__floater'
                      ) as HTMLDivElement;
                      if (floater) {
                        floater.style.display = 'none';
                      }
                    }}
                    className="button button--glass"
                    style={{ marginBottom: '1rem' }}
                  >
                    Finish
                  </button>
                )}
              </div>
            </div>
          ),
        };
      });
      setSteps(newSteps);
      setReady(true);
    }
  }, [stepTargets, onStepChange, onTourEnd]);

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { index, type, status } = data;

    if (type === 'step:before') {
      if (stepFunctions[index]) {
        stepFunctions[index]();
      }
    }

    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      if (onTourEnd) {
        onTourEnd();
      }
    }
  };

  if (!ready) {
    return null;
  }
  return (
    <>
      <Joyride
        steps={steps}
        run={run}
        stepIndex={stepIndex}
        callback={handleJoyrideCallback}
        continuous
        hideCloseButton={true}
        hideBackButton={true}
        disableScrolling={true}
        disableOverlay
        styles={styles}
      />
    </>
  );
};

export default HomeFeatureTour;
